"use client";

import * as React from "react";
import { useDict } from "@/components/i18n/DictProvider";
import { Eye, EyeOff } from "lucide-react";
import { Input } from "./Input";
import { cn } from "./cn";

type Props = Omit<React.InputHTMLAttributes<HTMLInputElement>, "type"> & {
  /** Locale for the toggle's accessible label. Defaults to SI (primary locale). */
};

/**
 * Password field with a show/hide eye toggle. Wraps the design-system Input, so
 * it inherits the same styling and forwards every input prop (name, value,
 * autoComplete, required, minLength, className, ref, …) — a drop-in replacement
 * for `<Input type="password">`. The toggle is a real <button> (keyboard-
 * operable, aria-pressed); it never submits the form (type="button").
 */
export const PasswordInput = React.forwardRef<HTMLInputElement, Props>(function PasswordInput(
  { className, ...props },
  ref,
) {
  const [visible, setVisible] = React.useState(false);
  const t = useDict();
  const label = visible ? t.auth.hidePassword : t.auth.showPassword;

  return (
    <div className="relative">
      <Input
        ref={ref}
        type={visible ? "text" : "password"}
        // pr-12 last so it wins the right-padding merge — keeps long values from
        // sliding under the toggle regardless of the caller's own padding.
        className={cn(className, "pr-12")}
        {...props}
      />
      <button
        type="button"
        onClick={() => setVisible((v) => !v)}
        aria-label={label}
        aria-pressed={visible}
        title={label}
        className="absolute right-0 top-0 h-full w-12 inline-flex items-center justify-center text-mid-gray hover:text-card-foreground focus-visible:text-blue focus-visible:outline-none"
      >
        {visible ? (
          <EyeOff className="size-5" aria-hidden />
        ) : (
          <Eye className="size-5" aria-hidden />
        )}
      </button>
    </div>
  );
});
